import { createFileRoute, Link } from "@tanstack/react-router";
import { Reveal } from "@/components/site/Reveal";
import { CtaPanel, PageHero } from "@/components/site/Sections";
import { blogPosts } from "@/lib/blog-posts";

const URL = "https://finendra.com/blog";
const OG = "https://finendra.com/og/og-blog.png";

const posts = blogPosts;

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Insights & Guides | Finendra" },
      {
        name: "description",
        content:
          "Practical guides on outsourced accounting, monthly close discipline, finance operations, and choosing the right back-office partner for growing businesses.",
      },
      { property: "og:title", content: "Finendra Insights & Guides" },
      {
        property: "og:description",
        content:
          "Guides on outsourced accounting, close discipline, and finance operations for growing businesses worldwide.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: OG },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: OG },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: BlogIndexPage,
});

function BlogIndexPage() {
  return (
    <>
      <PageHero
        eyebrow="Insights"
        title="Guides on running finance operations well."
        body="Field notes from running books, closes, and reporting for businesses worldwide — written for founders and finance leads, not for search engines."
      />

      <section className="px-6 pb-24">
        <div className="mx-auto grid max-w-7xl gap-8 md:grid-cols-2">
          {posts.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08}>
              <Link
                to={`/blog/${p.slug}` as "/blog/choosing-an-accounting-partner"}

                className="group flex h-full flex-col overflow-hidden rounded-3xl border border-brand-navy/10 bg-brand-surface transition-[border-color,box-shadow,transform] duration-200 ease-out hover:-translate-y-1 hover:border-brand-accent/40 hover:shadow-xl hover:shadow-brand-navy/5 active:scale-[0.985] active:duration-75 motion-reduce:transform-none select-none"
              >
                <img
                  src={p.featuredImage}
                  alt={p.title}
                  loading="lazy"
                  width={1600}
                  height={912}
                  className="h-52 w-full object-cover"
                />
                <div className="flex flex-1 flex-col p-8">
                  <p className="text-xs font-bold uppercase tracking-widest text-brand-accent">
                    {p.category}
                  </p>
                  <h2 className="mt-4 font-display text-2xl font-bold leading-snug text-brand-navy group-hover:text-brand-accent">
                    {p.title}
                  </h2>
                  <p className="mt-4 flex-1 leading-relaxed text-brand-navy/60">{p.excerpt}</p>
                  <p className="mt-6 text-sm font-semibold text-brand-navy/40">{p.readingTime}</p>
                </div>
              </Link>
            </Reveal>
          ))}

          <Reveal
            delay={0.16}
            className="flex flex-col justify-center rounded-3xl border border-dashed border-brand-navy/15 p-8 text-center"
          >
            <h2 className="font-display text-xl font-bold text-brand-navy">
              More guides in progress
            </h2>
            <p className="mt-3 text-brand-navy/60">
              We publish new material as we document the playbooks we run for clients. Tell us what
              would be useful and we will prioritise it.
            </p>
            <Link
              to="/contact"
              className="mx-auto mt-6 inline-block rounded-full bg-brand-navy px-6 py-3 text-sm font-semibold text-background transition-colors hover:bg-brand-accent"
            >
              Suggest a topic
            </Link>
          </Reveal>
        </div>
      </section>

      <CtaPanel />
    </>
  );
}
